/**
 * Combined Matrix Immunity & Distillation demo navigation registry
 * (RD-56 + RD-65).
 *
 * This app composes the two faces of the Matrix Immunity Theorem over
 * GL(n,ℂ): the four-condition eligibility audit (RD-56) and the VID
 * distillation to the greatest immune subset (RD-65). All panels — the
 * overview/foundation framing, the audit/collapse/conditions panels, and the
 * axiom/lab/convergence panels — now live locally in `../panels/`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { defineMonographNavigation } from "@csk/demo-ui/components/monograph-demo-shell";
import { DEMO_COLOR } from "@csk/demo-ui/design-system";
import { OverviewPanel } from "../panels/OverviewPanel.js";
import { ConditionsPanel } from "../panels/ConditionsPanel.js";
import { AuditLabPanel } from "../panels/AuditLabPanel.js";
import { CollapsePanel } from "../panels/CollapsePanel.js";
import { AxiomsPanel } from "../panels/AxiomsPanel.js";
import { DistillationLabPanel } from "../panels/DistillationLabPanel.js";
import { DistillationDefenderPanel } from "../panels/DistillationDefenderPanel.js";
import { ConvergencePanel } from "../panels/ConvergencePanel.js";
import { FoundationPanel } from "../panels/FoundationPanel.js";

export const NAV = [
  {
    id: "overview",
    label: "The Thesis",
    subtitle:
      "Audit eligibility, then distill the immune core — two faces of the MIT",
    icon: "◇",
    accent: DEMO_COLOR.violet,
    component: OverviewPanel,
  },
  {
    id: "conditions",
    label: "The Four Conditions",
    subtitle: "A1·A2·A3·A4 over G1–G4",
    icon: "⊞",
    accent: DEMO_COLOR.sky,
    component: ConditionsPanel,
  },
  {
    id: "audit",
    label: "Matrix Audit Lab",
    subtitle: "Run the four audits live on a GL(n,ℂ) matrix",
    icon: "▦",
    accent: DEMO_COLOR.gold,
    component: AuditLabPanel,
  },
  {
    id: "collapse",
    label: "Ten → Four Collapse",
    subtitle: "How A1–A4 imply the ten Neutralization Theorems",
    icon: "⇲",
    accent: DEMO_COLOR.cyan,
    component: CollapsePanel,
  },
  {
    id: "axioms",
    label: "MIT Axioms",
    subtitle:
      "A1 operator-supplied (violable) · A2/A3/A4 structural over GL(n,ℂ)",
    icon: "⊟",
    accent: DEMO_COLOR.indigo,
    component: AxiomsPanel,
  },
  {
    id: "lab",
    label: "Distillation Lab",
    subtitle: "Strip the A1 violators · watch μ descend to the immune core",
    icon: "⥥",
    accent: DEMO_COLOR.emerald,
    component: DistillationLabPanel,
  },
  {
    id: "defender",
    label: "Distillation Defender",
    subtitle: "Arcade VID · strip only A1 violators",
    icon: "◇",
    accent: DEMO_COLOR.pink,
    component: DistillationDefenderPanel,
  },
  {
    id: "convergence",
    label: "Convergence & Containment",
    subtitle: "Well-founded descent · greatest immune subset",
    icon: "Π",
    accent: DEMO_COLOR.success,
    component: ConvergencePanel,
  },
  {
    id: "foundation",
    label: "Foundation & Mechanization",
    subtitle: "GL(n,ℂ) realization · mechanization (RD-56 + RD-65)",
    icon: "λ",
    accent: DEMO_COLOR.teal,
    component: FoundationPanel,
  },
] as const;

export const NAVIGATION = defineMonographNavigation(NAV, {
  defaultPanelId: "overview",
});
